/**
 * Domain model shared by the backend (Lambda) and the frontend (React).
 *
 * Terminology maps onto the Cisco Meraki dashboard the UI is modelled after:
 *   Meraki organization  -> Tenant      (a client of the MSP)
 *   Meraki network       -> Site        (a UniFi console / location that hosts UniFi Protect)
 *   Meraki MT sensor     -> Device      (a Ubiquiti UniFi SuperLink "USL" sensor)
 *   Meraki alert profile -> AlertRule
 *   Meraki MSP portal    -> MSP overview (all tenants at a glance)
 */

// ---------------------------------------------------------------------------
// Identity & roles
// ---------------------------------------------------------------------------

export const ROLES = ['msp-admin', 'tenant-admin', 'tenant-viewer'] as const;
export type Role = (typeof ROLES)[number];

/** Cognito group names. They are identical to the role names on purpose. */
export const COGNITO_GROUPS: Record<Role, string> = {
  'msp-admin': 'msp-admin',
  'tenant-admin': 'tenant-admin',
  'tenant-viewer': 'tenant-viewer',
};

/** Custom Cognito attribute that pins a user to a tenant. Empty for MSP admins. */
export const TENANT_ID_ATTRIBUTE = 'custom:tenantId';

export interface Me {
  sub: string;
  email: string;
  role: Role;
  isMsp: boolean;
  /** Set for tenant users only. */
  tenantId?: string;
  /** Tenants the caller may open. MSP admins get every active tenant. */
  tenants: TenantSummary[];
}

// ---------------------------------------------------------------------------
// Tenants (MSP clients)
// ---------------------------------------------------------------------------

export type TenantStatus = 'active' | 'suspended';

export interface Tenant {
  tenantId: string;
  name: string;
  /** URL friendly, unique, immutable. */
  slug: string;
  status: TenantStatus;
  contactName?: string;
  contactEmail?: string;
  timezone?: string;
  notes?: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export type TenantSummary = Pick<Tenant, 'tenantId' | 'name' | 'slug' | 'status'>;

export interface CreateTenantInput {
  name: string;
  slug?: string;
  contactName?: string;
  contactEmail?: string;
  timezone?: string;
  notes?: string;
  tags?: string[];
}

export type UpdateTenantInput = Partial<Omit<CreateTenantInput, 'slug'>> & {
  status?: TenantStatus;
};

// ---------------------------------------------------------------------------
// Sites (UniFi consoles)
// ---------------------------------------------------------------------------

export type SiteStatus = 'online' | 'offline' | 'unknown';

export interface Site {
  tenantId: string;
  /** Stable id: the UniFi console id reported by the Site Manager API. */
  siteId: string;
  name: string;
  source: 'unifi-protect';
  consoleId: string;
  hostType?: string;
  ipAddress?: string;
  firmwareVersion?: string;
  protectVersion?: string;
  status: SiteStatus;
  lastSyncAt?: string;
  lastSyncError?: string;
  deviceCount: number;
  location?: string;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Devices (USL sensors)
// ---------------------------------------------------------------------------

/**
 * Functional classification of a device. The UniFi model name is kept on the
 * device as well, this is what the UI uses to pick icons and metric cards.
 */
export type DeviceKind =
  | 'environmental' // USL-Environmental / UP-Sense: temperature, humidity, light, leak
  | 'entry' // USL-Entry: door / window open-closed
  | 'motion' // USL-Motion
  | 'relay' // USL-Relay: I/O relay for third-party sensors
  | 'gateway' // USL-Gateway (SuperLink gateway)
  | 'sensor' // any other Protect sensor
  | 'camera' // UniFi Protect camera (UVC / G4 / G5 ...)
  | 'doorbell' // UniFi Protect doorbell: a camera with a chime and an LCD
  | 'unknown';

/** Kinds shown on the Sensors pages. */
export const SENSOR_KINDS: DeviceKind[] = [
  'environmental',
  'entry',
  'motion',
  'relay',
  'gateway',
  'sensor',
  'unknown',
];

/** Kinds shown on the Cameras pages. */
export const CAMERA_KINDS: DeviceKind[] = ['camera', 'doorbell'];

export const DEVICE_KINDS: DeviceKind[] = [...SENSOR_KINDS, ...CAMERA_KINDS];

/**
 * Cameras and sensors share the Device record (and therefore sites, alerting,
 * tagging and the stale sweep) but are listed on separate pages, because a
 * camera reports none of the sensor readings and vice versa.
 */
export function isCameraKind(kind: DeviceKind): boolean {
  return CAMERA_KINDS.includes(kind);
}

/** Same vocabulary as the Meraki device status pill. */
export type DeviceStatus = 'online' | 'alerting' | 'offline' | 'dormant';

export const DEVICE_STATUSES: DeviceStatus[] = ['online', 'alerting', 'offline', 'dormant'];

export interface DeviceMetrics {
  temperature?: number; // Celsius
  humidity?: number; // percent RH
  light?: number; // lux
  batteryPercentage?: number;
  batteryLow?: boolean;
  isOpened?: boolean;
  motionDetected?: boolean;
  leakDetected?: boolean;
  tamperDetected?: boolean;
  alarmTriggered?: boolean;
  signalQuality?: number;
  signalStrength?: number; // dBm
}

/**
 * What the Protect integration API tells us about a camera. Every field is
 * optional: the payload varies by model and Protect version, and a doorbell
 * reports keys a bullet camera never will.
 */
export interface CameraInfo {
  /** Protect video mode, e.g. "default" or "highFps". */
  videoMode?: string;
  /** HDR mode as Protect reports it, e.g. "auto" or "off". */
  hdrMode?: string;
  hasHdr?: boolean;
  hasMic?: boolean;
  hasSpeaker?: boolean;
  hasLedStatus?: boolean;
  /** Doorbells with a second, downward-facing lens for parcels. */
  hasPackageCamera?: boolean;
  micEnabled?: boolean;
  /** True when the camera can serve a full-resolution still, not just a preview. */
  supportsFullHdSnapshot?: boolean;
  /** Object types the camera is set to detect, e.g. ["person", "vehicle"]. */
  smartDetectTypes?: string[];
  /** Message currently shown on a doorbell's LCD. */
  lcdMessage?: string;
}

export type MetricKey = keyof DeviceMetrics;

export interface DeviceTimestamps {
  openStatusChangedAt?: string;
  motionDetectedAt?: string;
  leakDetectedAt?: string;
  alarmTriggeredAt?: string;
  tamperingDetectedAt?: string;
}

export interface Device {
  tenantId: string;
  siteId: string;
  /** Vendor id (UniFi Protect sensor id). Stable across syncs. */
  deviceId: string;
  name: string;
  model: string;
  kind: DeviceKind;
  vendor: 'ubiquiti';
  source: 'unifi-protect';
  mac?: string;
  firmwareVersion?: string;
  hardwareRevision?: string;
  /** door | window | garage | leak | none (UniFi vocabulary) */
  mountType?: string;
  status: DeviceStatus;
  /** Raw vendor connection state, e.g. CONNECTED / DISCONNECTED */
  connectionState?: string;
  lastSeenAt?: string;
  lastSyncAt: string;
  upSince?: string;
  /** Set while the device is offline; used by the offline alert rule. */
  offlineSince?: string;
  metrics: DeviceMetrics;
  /** Only set on camera and doorbell devices. */
  camera?: CameraInfo;
  timestamps: DeviceTimestamps;
  activeAlertCount: number;
  /** ruleId -> alertId for alerts currently open on this device. */
  openAlerts?: Record<string, string>;
  tags: string[];
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateDeviceInput {
  name?: string;
  tags?: string[];
  notes?: string;
}

/** One time-series sample for a device. */
export interface Reading {
  deviceId: string;
  ts: string;
  metrics: Partial<Record<MetricKey, number | boolean>>;
}

// ---------------------------------------------------------------------------
// Alerts
// ---------------------------------------------------------------------------

export type AlertSeverity = 'critical' | 'warning' | 'info';
export type AlertStatus = 'open' | 'resolved';

export type AlertRuleKind =
  | 'threshold' // numeric metric compared with a threshold
  | 'leak'
  | 'battery-low'
  | 'offline'
  | 'door-open' // entry sensor open for longer than durationMinutes
  | 'motion'
  | 'tamper';

export type ThresholdMetric = 'temperature' | 'humidity' | 'light' | 'batteryPercentage';
export type ThresholdOperator = 'gt' | 'gte' | 'lt' | 'lte';

export interface AlertRule {
  tenantId: string;
  ruleId: string;
  name: string;
  kind: AlertRuleKind;
  enabled: boolean;
  severity: AlertSeverity;
  metric?: ThresholdMetric;
  operator?: ThresholdOperator;
  threshold?: number;
  /** Used by door-open and offline rules. */
  durationMinutes?: number;
  /** Empty array = every device in the tenant. */
  deviceIds: string[];
  /** Empty array = every site in the tenant. */
  siteIds: string[];
  notifyEmails: string[];
  /** Created automatically with the tenant; can be disabled but not deleted. */
  builtIn: boolean;
  createdAt: string;
  updatedAt: string;
}

export type AlertRuleInput = Omit<
  AlertRule,
  'tenantId' | 'ruleId' | 'createdAt' | 'updatedAt' | 'builtIn'
>;

export interface Alert {
  tenantId: string;
  alertId: string;
  ruleId: string;
  ruleName: string;
  kind: AlertRuleKind;
  deviceId: string;
  deviceName: string;
  siteId?: string;
  severity: AlertSeverity;
  status: AlertStatus;
  message: string;
  metric?: string;
  value?: number | boolean;
  openedAt: string;
  resolvedAt?: string;
  resolvedBy?: string;
  lastEvaluatedAt: string;
}

// ---------------------------------------------------------------------------
// Events (event log)
// ---------------------------------------------------------------------------

export type EventType = 'webhook' | 'sync' | 'alert' | 'audit' | 'system';

export interface EventRecord {
  tenantId: string;
  eventId: string;
  ts: string;
  type: EventType;
  source: string;
  title: string;
  severity?: AlertSeverity;
  deviceId?: string;
  deviceName?: string;
  actor?: string;
  payload?: unknown;
}

// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------

export interface TenantUser {
  tenantId: string;
  /** Cognito username (the sub). */
  username: string;
  email: string;
  role: Role;
  /** Cognito user status, e.g. FORCE_CHANGE_PASSWORD, CONFIRMED. */
  userStatus: string;
  enabled: boolean;
  /** True when the user has an authenticator app (TOTP) registered. */
  mfaEnabled?: boolean;
  invitedAt: string;
  invitedBy: string;
}

export interface InviteUserInput {
  email: string;
  role: Exclude<Role, 'msp-admin'>;
}

// ---------------------------------------------------------------------------
// Integrations
// ---------------------------------------------------------------------------

export type SyncStatus = 'ok' | 'error' | 'running' | 'never';

/**
 * One licence plate read by a UniFi Protect camera.
 *
 * Protect does not offer a REST endpoint for detections: the integration API
 * streams them over a WebSocket, which a scheduled Lambda cannot hold open. So
 * plates reach the dashboard by push, from an Alarm Manager rule pointed at the
 * tenant's webhook URL, and are stored as they arrive.
 */
export interface PlateDetection {
  tenantId: string;
  /** Time-prefixed so DynamoDB sort order is chronological. */
  detectionId: string;
  /** When Protect saw the vehicle. */
  ts: string;
  /** When the dashboard received the webhook. */
  receivedAt: string;
  /**
   * Uppercase, letters and digits only; what searches compare against. When
   * Protect sent only the vehicle's name and no plate is saved for it
   * (`plateSource: 'name'`), this is the name compared the same way, so the
   * vehicle's reads still group and search together.
   */
  plate: string;
  /** Exactly as the payload spelled it, spacing included. Empty when Protect sent only a name. */
  rawPlate: string;
  /**
   * The name a known vehicle is saved under in Protect, e.g. "GS Tesla".
   * Protect sends this instead of the plate when it recognises a vehicle on
   * one of its lists.
   */
  vehicleName?: string;
  /** `vehicleKey(vehicleName)`: what name searches compare against, and how its plate is saved. */
  vehicleKey?: string;
  /**
   * Set when Protect sent only the vehicle's name. "name": no plate is saved
   * for that name yet. "saved": `plate` and `rawPlate` are the plate an
   * administrator saved for the name, filled in as the read is listed.
   */
  plateSource?: 'name' | 'saved';
  deviceId?: string;
  deviceName?: string;
  siteId?: string;
  /** The Alarm Manager rule that fired, e.g. "Car park plates". */
  alarmName?: string;
  /** Protect's trigger key, e.g. "license_plate_known". */
  triggerKey?: string;
  /** True when Protect matched a vehicle on a list, false when it did not. */
  known?: boolean;
  /** Where in the payload the plate was found; kept for diagnosing new shapes. */
  field?: string;
  /** Deep link into Protect for the clip, when the payload carries one. */
  eventLink?: string;
  /**
   * True when Protect sent a picture with the read ("Use thumbnails" on the
   * alarm). The picture is kept in S3 and fetched on its own, never inline.
   */
  hasThumbnail?: boolean;
  expiresAt?: number;
}

/** Plates compare on letters and digits only: "1AB 2CD" and "1ab-2cd" are one vehicle. */
export function normalisePlate(raw: string): string {
  return raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/** Plates are short and alphanumeric; separators are allowed but nothing else. */
const PLATE_SHAPE = /^[A-Z0-9][A-Z0-9 .·-]{1,11}$/i;

/** Words that pass the shape test but are never a plate. */
const NOT_A_PLATE =
  /^(person|people|vehicle|car|truck|animal|package|parcel|face|motion|audio|smoke|alarm|known|unknown|true|false|null|none|nan)$/i;

/** True for text that could be a licence plate, whether read by a camera or typed in. */
export function looksLikePlate(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length < 2 || trimmed.length > 12) return false;
  if (!PLATE_SHAPE.test(trimmed)) return false;
  if (NOT_A_PLATE.test(trimmed)) return false;
  // Require at least one digit or two letters, which rules out "A" or "-".
  return normalisePlate(trimmed).length >= 2;
}

/**
 * Vehicle names compare as plates do, on letters and digits only, but keep
 * letters of any script and drop accents: "GS Tesla" and "gstesla" are one
 * vehicle, as are "Zoë's car" and "zoes car". A name in plain Latin letters
 * comes out exactly as normalisePlate would make it.
 */
export function vehicleKey(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toUpperCase()
    .replace(/[^\p{L}\p{N}]/gu, '');
}

/**
 * The plate an administrator saved for a vehicle Protect knows by name.
 * Protect's webhook names a vehicle on its known list rather than giving its
 * plate, so the plate is saved once here and every read of that name, earlier
 * and later, shows it.
 */
export interface NamedVehicle {
  tenantId: string;
  /** `vehicleKey(name)`; one entry per key. */
  vehicleKey: string;
  /** The name as Protect sends it. */
  name: string;
  /** Normalised, as on a read. */
  plate: string;
  /** As the administrator typed it, in capitals. */
  rawPlate: string;
  updatedAt: string;
  updatedBy: string;
}

export interface SaveVehiclePlateInput {
  /** The vehicle's name as Protect sends it. */
  name: string;
  plate: string;
}

/**
 * A read as the dashboard shows it: one that came with only a vehicle's name
 * takes the plate saved for that name, when there is one.
 */
export function withSavedPlate(
  read: PlateDetection,
  saved: ReadonlyMap<string, Pick<NamedVehicle, 'plate' | 'rawPlate'>>,
): PlateDetection {
  if (read.plateSource !== 'name' || !read.vehicleKey) return read;
  const vehicle = saved.get(read.vehicleKey);
  return vehicle
    ? { ...read, plate: vehicle.plate, rawPlate: vehicle.rawPlate, plateSource: 'saved' }
    : read;
}

/** A search box's text as plates and names compare it. */
export interface PlateSearchTerms {
  plate: string;
  name: string;
}

/** Undefined when the text holds no letter or digit, which searches for nothing. */
export function plateSearchTerms(text: string | undefined): PlateSearchTerms | undefined {
  const plate = normalisePlate(text ?? '');
  const name = vehicleKey(text ?? '');
  return plate || name ? { plate, name } : undefined;
}

/**
 * The plates saved for named vehicles whose name contains the text. Every read
 * of such a plate answers the search, so "tesla" also finds the car's reads
 * from before it was on a list, when Protect still sent its plate.
 */
export function platesNamedBy(
  terms: PlateSearchTerms,
  saved: Iterable<Pick<NamedVehicle, 'vehicleKey' | 'plate'>>,
): Set<string> {
  const plates = new Set<string>();
  if (!terms.name) return plates;
  for (const v of saved) if (v.vehicleKey.includes(terms.name)) plates.add(v.plate);
  return plates;
}

/**
 * True when a read, as shown, answers a search: the text is part of its plate
 * or of the name Protect knows the vehicle by, or it is a read of a plate
 * saved for a vehicle whose name matches (see platesNamedBy). "EFK" finds
 * EFK042, and so does "tesla" once EFK042 is saved for "GS Tesla".
 */
export function matchesPlateSearch(
  read: Pick<PlateDetection, 'plate' | 'vehicleName'>,
  terms: PlateSearchTerms,
  namedPlates: ReadonlySet<string> = new Set(),
): boolean {
  if (terms.plate && read.plate.includes(terms.plate)) return true;
  if (namedPlates.has(read.plate)) return true;
  return Boolean(terms.name && read.vehicleName && vehicleKey(read.vehicleName).includes(terms.name));
}

/** Groups repeat visits of the same vehicle for the review list. */
export interface PlateSighting {
  plate: string;
  /** The name Protect gave the vehicle on its newest read that had one. */
  vehicleName?: string;
  vehicleKey?: string;
  /** From that same read; "name" means the vehicle has no saved plate yet. */
  plateSource?: 'name' | 'saved';
  count: number;
  firstSeen: string;
  lastSeen: string;
  cameras: string[];
}

/** Groups reads into one row per vehicle, newest visit first. */
export function groupPlateReads(reads: PlateDetection[]): PlateSighting[] {
  const byPlate = new Map<string, PlateSighting>();
  // When each row's name was read, so the newest name wins whatever the order.
  const namedAt = new Map<string, string>();
  for (const d of reads) {
    let row = byPlate.get(d.plate);
    if (!row) {
      row = { plate: d.plate, count: 0, firstSeen: d.ts, lastSeen: d.ts, cameras: [] };
      byPlate.set(d.plate, row);
    }
    row.count += 1;
    if (d.ts < row.firstSeen) row.firstSeen = d.ts;
    if (d.ts > row.lastSeen) row.lastSeen = d.ts;
    if (d.deviceName && !row.cameras.includes(d.deviceName)) row.cameras.push(d.deviceName);
    if (d.vehicleName && d.ts >= (namedAt.get(d.plate) ?? '')) {
      namedAt.set(d.plate, d.ts);
      row.vehicleName = d.vehicleName;
      row.vehicleKey = d.vehicleKey;
      row.plateSource = d.plateSource;
    }
  }
  return [...byPlate.values()].sort((a, b) => b.lastSeen.localeCompare(a.lastSeen));
}

/**
 * The most plate reads one request returns. Reads are sent whole, so 2,000 of
 * them and their vehicle rows come to about 2 MB, well inside the 6 MB a
 * Lambda response may carry.
 */
export const PLATE_READS_MAX = 2000;

export interface UnifiIntegration {
  tenantId: string;
  enabled: boolean;
  hasApiKey: boolean;
  apiKeyLast4?: string;
  /**
   * UniFi console ids (Site Manager host ids) that belong to this tenant.
   * Empty = every console the API key can see. Organisation-level keys that
   * span several clients must list the consoles explicitly.
   */
  consoleIds: string[];
  /** Secret path segment for the Alarm Manager webhook URL. Admins only. */
  webhookToken?: string;
  webhookUrl?: string;
  syncIntervalMinutes: number;
  lastSyncAt?: string;
  lastSyncStatus: SyncStatus;
  lastSyncError?: string;
  lastSyncDeviceCount?: number;
  updatedAt: string;
  updatedBy: string;
}

export interface UpdateUnifiIntegrationInput {
  /** Omit to keep the stored key. */
  apiKey?: string;
  consoleIds?: string[];
  enabled?: boolean;
  syncIntervalMinutes?: number;
  rotateWebhookToken?: boolean;
}

/** A UniFi OS console as discovered through the Site Manager API. */
export interface UnifiConsole {
  id: string;
  name: string;
  type?: string;
  ipAddress?: string;
  firmwareVersion?: string;
  isOnline?: boolean;
  hasProtect: boolean;
  protectVersion?: string;
}

export interface IntegrationTestResult {
  ok: boolean;
  message: string;
  consoles: UnifiConsole[];
}

// ---------------------------------------------------------------------------
// Aggregates for dashboard pages
// ---------------------------------------------------------------------------

export type StatusCounts = Record<DeviceStatus, number>;

export const emptyStatusCounts = (): StatusCounts => ({
  online: 0,
  alerting: 0,
  offline: 0,
  dormant: 0,
});

export interface TenantOverview {
  tenant: Tenant;
  counts: {
    devices: StatusCounts;
    byKind: Partial<Record<DeviceKind, number>>;
    sites: number;
    openAlerts: number;
  };
  sites: Site[];
  recentAlerts: Alert[];
  recentEvents: EventRecord[];
  integration?: Pick<
    UnifiIntegration,
    'enabled' | 'hasApiKey' | 'lastSyncAt' | 'lastSyncStatus' | 'lastSyncError'
  >;
}

export interface MspTenantSummary {
  tenant: TenantSummary;
  devices: StatusCounts;
  totalDevices: number;
  sites: number;
  openAlerts: number;
  lastSyncAt?: string;
  lastSyncStatus: SyncStatus;
}

export interface MspOverview {
  tenants: MspTenantSummary[];
  totals: {
    tenants: number;
    devices: StatusCounts;
    openAlerts: number;
  };
}

// ---------------------------------------------------------------------------
// Generic API envelope
// ---------------------------------------------------------------------------

export interface ListResponse<T> {
  items: T[];
  nextToken?: string;
}

export interface ApiError {
  error: string;
  message: string;
  details?: unknown;
}

// ---------------------------------------------------------------------------
// Helpers shared by both sides
// ---------------------------------------------------------------------------

/** Maps a UniFi model / market name to our functional device kind. */
export function deviceKindFromModel(model: string | undefined, mountType?: string): DeviceKind {
  const m = (model ?? '').toUpperCase();
  if (m.includes('USL-ENV') || m.includes('UP-SENSE') || m.includes('UFP-SENSE')) {
    return 'environmental';
  }
  if (m.includes('USL-ENTRY')) return 'entry';
  if (m.includes('USL-MOTION')) return 'motion';
  if (m.includes('USL-RELAY')) return 'relay';
  if (m.includes('USL-GATEWAY') || m.includes('SUPERLINK GATEWAY')) return 'gateway';
  if (mountType === 'door' || mountType === 'window' || mountType === 'garage') return 'entry';
  if (m.includes('SENSE') || m.includes('SENSOR')) return 'sensor';
  return m ? 'unknown' : 'unknown';
}

/**
 * Cameras are read from a different Protect endpoint than sensors, so the
 * caller already knows it holds a camera; this only separates doorbells, which
 * get a chime and an LCD message the plain cameras do not have.
 */
export function cameraKindFromModel(model: string | undefined): DeviceKind {
  const m = (model ?? '').toUpperCase();
  return m.includes('DOORBELL') || m.includes('-DB') ? 'doorbell' : 'camera';
}

export const KIND_LABELS: Record<DeviceKind, string> = {
  environmental: 'Environmental',
  entry: 'Entry (door/window)',
  motion: 'Motion',
  relay: 'Relay (I/O)',
  gateway: 'Sensor gateway',
  sensor: 'Sensor',
  camera: 'Camera',
  doorbell: 'Doorbell',
  unknown: 'Unknown',
};

export const STATUS_LABELS: Record<DeviceStatus, string> = {
  online: 'Online',
  alerting: 'Alerting',
  offline: 'Offline',
  dormant: 'Dormant',
};
